import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/navigation/Sidebar';
import NavigationBreadcrumb from '../../components/navigation/NavigationBreadcrumb';
import NotificationIndicator from '../../components/navigation/NotificationIndicator';
import MetricCard from './components/MetricCard';
import CalendarWidget from './components/CalendarWidget';
import QuickActions from './components/QuickActions';
import ActivityFeed from './components/ActivityFeed';
import AdminStatistics from './components/AdminStatistics';

const Dashboard = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  const [direction, setDirection] = useState(() => {
    return localStorage.getItem('direction') || 'ltr';
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole] = useState('lawyer');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'fr';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const metrics = [
    {
      id: 'active-cases',
      titleEn: 'Active Cases',
      titleFr: 'Dossiers Actifs',
      titleAr: 'القضايا النشطة',
      value: 24,
      icon: 'Briefcase',
      iconColor: 'bg-primary/10 text-primary',
      trend: 'up',
      trendValue: 12,
      navigateTo: '/case-management'
    },
    {
      id: 'upcoming-hearings',
      titleEn: 'Upcoming Hearings',
      titleFr: 'Audiences à Venir',
      titleAr: 'الجلسات القادمة',
      value: 8,
      icon: 'Calendar',
      iconColor: 'bg-warning/10 text-warning',
      trend: 'up',
      trendValue: 3,
      navigateTo: '/calendar-scheduling'
    },
    {
      id: 'pending-invoices',
      titleEn: 'Pending Invoices',
      titleFr: 'Factures en Attente',
      titleAr: 'الفواتير المعلقة',
      value: 5,
      icon: 'Receipt',
      iconColor: 'bg-error/10 text-error',
      trend: 'down',
      trendValue: 2,
      navigateTo: '/billing-invoice'
    },
    {
      id: 'total-clients',
      titleEn: 'Total Clients',
      titleFr: 'Total Clients',
      titleAr: 'إجمالي العملاء',
      value: 156,
      icon: 'Users',
      iconColor: 'bg-success/10 text-success',
      trend: 'up',
      trendValue: 8,
      navigateTo: '/client-management'
    }
  ];

  const welcomeMessage = {
    en: 'Welcome back',
    fr: 'Bon retour',
    ar: 'مرحبا بعودتك'
  };

  const dateLabel = {
    en: 'Today',
    fr: "Aujourd\'hui",
    ar: 'اليوم'
  };

  const formatDate = () => {
    const today = new Date();
    if (language === 'ar') {
      return new Intl.DateTimeFormat('ar-MA', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })?.format(today);
    }
    return new Intl.DateTimeFormat('fr-MA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })?.format(today);
  };

  return (
    <div className="min-h-screen bg-background" style={{ direction }}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-all duration-250 pt-16 lg:pt-0 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h1 className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold text-foreground mb-1">
                  {welcomeMessage?.[language]}, Maitre Moatamid
                </h1>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {dateLabel?.[language]} - {formatDate()}
                </p>
              </div>
              <NotificationIndicator language={language} />
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <NavigationBreadcrumb language={language} />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {metrics?.map((metric) => (
              <MetricCard
                key={metric?.id}
                {...metric}
                language={language}
              />
            ))}
          </div>

          <div className="mb-6">
            <QuickActions language={language} userRole={userRole} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <CalendarWidget language={language} direction={direction} />
            </div>
            <div className="lg:col-span-1">
              <ActivityFeed language={language} direction={direction} />
            </div>
          </div>

          {userRole === 'administrator' && (
            <div className="mb-6">
              <AdminStatistics language={language} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;